import { PageIcon } from "@/components/PageIcon";
import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Loader2, RefreshCw, TrendingUp, BarChart3, Lightbulb, Tags, AlertTriangle } from "lucide-react";
import { Card } from "@/components/analytics/AnalyticsParts";
import { formatDateTime } from "@/lib/datetime";
import { InterestChart, SalesVsTrend, RelatedSearches, KeywordEditor } from "@/components/trends/TrendsParts";

const errMsg = (e) => { const d = e.response?.data?.detail; return typeof d === "string" ? d : "Request failed"; };

export default function GoogleTrendsPage() {
  const { can } = useAuth();
  const [d, setD] = useState(null);
  const [saving, setSaving] = useState(false);
  const load = useCallback(() => api.get("/marketing/trends").then((r) => setD(r.data)).catch((e) => toast.error(errMsg(e))), []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (d?.status !== "fetching") return undefined;
    const t = setTimeout(load, 5000);
    return () => clearTimeout(t);
  }, [d, load]);
  const refresh = async () => { try { await api.post("/marketing/trends/refresh"); load(); } catch (e) { toast.error(errMsg(e)); } };
  const save = async (keywords) => {
    setSaving(true);
    try { await api.put("/marketing/trends/keywords", { keywords }); toast.success("Keywords saved, fetching trends"); load(); } catch (e) { toast.error(errMsg(e)); }
    finally { setSaving(false); }
  };
  if (!d) return <div className="p-8"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>;
  const kws = d.data_keywords || [];
  const hasData = d.monthly.length > 0;
  return (
    <div className="px-6 lg:px-8 pt-3 lg:pt-4 pb-6 lg:pb-8 space-y-5" data-testid="google-trends-page">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex-1 min-w-[200px]">
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight flex items-center gap-3"><PageIcon />Google Trends</h1>
          <p className="text-sm text-slate-500 mt-1" data-testid="trends-meta">UK search interest, last 5 years{d.fetched_at ? ` · updated ${formatDateTime(d.fetched_at)}` : ""}</p>
        </div>
        {can("customers.edit") && (
          <Button variant="outline" className="gap-1.5" onClick={refresh} disabled={d.status === "fetching"} data-testid="trends-refresh-btn">
            {d.status === "fetching" ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}{d.status === "fetching" ? "Fetching…" : "Refresh"}
          </Button>
        )}
      </div>
      {d.status === "error" && d.error && <p className="flex items-center gap-2 text-sm text-amber-800 bg-amber-50 rounded-xl px-4 py-3" data-testid="trends-error"><AlertTriangle className="w-4 h-4 shrink-0" />{d.error}{hasData ? " Showing the last saved data." : ""}</p>}
      {!hasData ? (
        <Card title="Search interest" icon={TrendingUp} testid="trends-empty"><p className="text-sm text-slate-500">{d.status === "fetching" ? "Fetching data from Google Trends. This can take up to a minute." : "No trends data yet."}</p></Card>
      ) : (
        <>
          <Card title="Search interest over time" icon={TrendingUp} testid="trends-interest-card"><InterestChart monthly={d.monthly} keywords={kws} /></Card>
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
            <Card title="Searches vs your sales" icon={BarChart3} testid="trends-sales-section"><SalesVsTrend monthly={d.monthly} keywords={kws} categories={d.categories} sales={d.sales} /></Card>
            <Card title="Related searches" icon={Lightbulb} testid="trends-related-section"><RelatedSearches keywords={kws} rising={d.rising} top={d.top} /></Card>
          </div>
        </>
      )}
      {can("customers.edit") && <Card title="Tracked keywords" icon={Tags} testid="trends-keywords-section"><KeywordEditor key={d.keywords.join("|")} keywords={d.keywords} onSave={save} saving={saving} /></Card>}
    </div>
  );
}
